import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/apiClient';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectLabel,
} from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Users, Wrench, ChevronDown, ChevronUp, Box, AlertCircle, UserCog } from 'lucide-react';
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

// Opciones genéricas de asignación por tipo de operario
const GENERIC_OPERATOR_OPTIONS = [
  { id: 'skill_level:A', label: 'Operario tipo A', skillLevel: 'A', description: 'Asigna a cualquier operario nivel A' },
  { id: 'skill_level:B', label: 'Operario tipo B', skillLevel: 'B', description: 'Asigna a cualquier operario nivel B' },
  { id: 'skill_level:C', label: 'Operario tipo C', skillLevel: 'C', description: 'Asigna a cualquier operario nivel C' },
  { id: 'skill_level:any', label: 'Cualquier operario', skillLevel: null, description: 'Asigna al primer operario disponible' },
];

interface ProcessAssignment {
  processId: string;
  operatorId: string;
  itemId?: string;  // null = asignación global, UUID = pieza específica
}

interface WorkOrderItem {
  id: string;
  item_number: number;
  part_label?: string | null;
}

interface AssignmentRule {
  process_code: string;
  process_id: string | null;
  assignment_type: 'required' | 'optional' | 'pool' | 'none';
  min_skill_level: string | null;
  allowed_skill_levels: string[];
  assignment_description: string | null;
}

interface ProcessAssignmentsProps {
  workflowType: 'plotter_only' | 'plotter_first' | 'direct_lamination' | 'standard' | 'internal';
  assignments: ProcessAssignment[];
  onAssignmentsChange: (assignments: ProcessAssignment[]) => void;
  workOrderItems?: WorkOrderItem[];
  onValidationChange?: (isValid: boolean, missingRequired: string[]) => void;
}

// Badge de nivel de habilidad
const getSkillBadge = (level: 'A' | 'B' | 'C' | null) => {
  if (!level) return null;
  const config: Record<string, { color: string; title: string }> = {
    A: { color: 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-400', title: 'Experto' },
    B: { color: 'bg-amber-500/20 text-amber-700 dark:text-amber-400', title: 'Intermedio' },
    C: { color: 'bg-slate-500/20 text-slate-700 dark:text-slate-400', title: 'Básico' },
  };
  return (
    <Badge className={`${config[level].color} text-[10px] ml-1 px-1 py-0`} title={config[level].title}>
      {level}
    </Badge>
  );
};

// Procesos ordenados según el flujo de producción
const WORKFLOW_PROCESSES: Record<string, string[]> = {
  plotter_only: [
    'material_cutting',      // Solo corte plotter (unico proceso)
  ],
  plotter_first: [
    'material_cutting',      // Corte de material (plotter)
    'material_layup',        // Laminado
    'vacuum_bagging',        // Bolsa de vacío
    'autoclave_curing',      // Curado en autoclave
    'demolding',             // Desmolde
    'trimming',              // Recorte/rebabado
    'machining',             // Mecanizado
    'inspection',            // Inspección
    'painting',              // Pintura
    'final_assembly',        // Ensamble final
    'final_inspection',      // Inspección final
    'dispatch',              // Despacho
  ],
  direct_lamination: [
    'material_layup',        // Laminado (inicia aquí)
    'vacuum_bagging',
    'autoclave_curing',
    'demolding',
    'trimming',
    'machining',
    'inspection',
    'painting',
    'final_assembly',
    'final_inspection',
    'dispatch',
  ],
  standard: [
    'material_cutting',
    'material_layup',
    'vacuum_bagging',
    'autoclave_curing',
    'demolding',
    'trimming',
    'machining',
    'inspection',
    'painting',
    'final_assembly',
    'final_inspection',
    'dispatch',
  ],
};

export function ProcessAssignments({
  workflowType,
  assignments,
  onAssignmentsChange,
  workOrderItems,
  onValidationChange,
}: ProcessAssignmentsProps) {
  const [expanded, setExpanded] = useState(false);
  const [skillFilter, setSkillFilter] = useState<'all' | 'A' | 'B' | 'C'>('all');
  const [viewMode, setViewMode] = useState<'process' | 'item'>('process');

  // Con Corte Plotter como proceso inicial, la OT solo abarca el corte:
  // únicamente se asigna operario a material_cutting; el resto de procesos
  // se asignarán en la OT de laminado que consuma el paquete del congelador.
  const isPlotterScope = workflowType === 'plotter_first' || workflowType === 'plotter_only';
  const isProcessLocked = (processCode: string) =>
    isPlotterScope && processCode !== 'material_cutting';

  // Mostrar vista por pieza solo si hay más de 1 item
  const showItemView = workOrderItems && workOrderItems.length > 1;

  // Fetch manufacturing processes
  const { data: processes } = useQuery({
    queryKey: ['manufacturing-processes'],
    queryFn: async () => {
      const { data, error } = await api
        .from('manufacturing_processes')
        .select('id, name, code, description')
        .eq('is_active', true)
        .order('sequence_order', { ascending: true });
      if (error) throw error;
      return data;
    },
  });

  // Fetch assignment rules
  const { data: assignmentRules } = useQuery({
    queryKey: ['process-assignment-rules'],
    queryFn: async () => {
      const { data, error } = await api
        .from('process_assignment_rules')
        .select('process_code, process_id, assignment_type, min_skill_level, allowed_skill_levels, assignment_description')
        .eq('is_active', true);
      if (error) throw error;
      return data as AssignmentRule[];
    },
  });

  // Fetch operators
  const { data: operators } = useQuery({
    queryKey: ['operators-for-assignment'],
    queryFn: async () => {
      const { data, error } = await api
        .from('profiles')
        .select('id, full_name, employee_id, skill_level')
        .eq('is_active', true)
        .order('full_name', { ascending: true });
      if (error) throw error;
      return data;
    },
  });

  // Get rule for a process by code
  const getRuleForProcess = (processCode: string): AssignmentRule | undefined => {
    return assignmentRules?.find(r => r.process_code === processCode);
  };

  // Filter operators by skill level (global filter) and by process allowed skills
  const getOperatorsForProcess = (processCode: string) => {
    const rule = getRuleForProcess(processCode);
    return operators?.filter((op: any) => {
      // Filtro global por nivel de habilidad
      const matchesGlobalFilter = skillFilter === 'all' || op.skill_level === skillFilter;
      // Si no hay regla o no tiene niveles permitidos definidos, mostrar todos
      if (!rule || !rule.allowed_skill_levels || rule.allowed_skill_levels.length === 0) {
        return matchesGlobalFilter;
      }
      // Si hay regla con niveles permitidos, verificar que el operador tenga nivel y esté permitido
      const matchesProcessSkill = !op.skill_level || rule.allowed_skill_levels.includes(op.skill_level);
      return matchesGlobalFilter && matchesProcessSkill;
    });
  };

  // Filter operators by skill level (for backwards compatibility)
  const filteredOperators = operators?.filter((op: any) =>
    skillFilter === 'all' || op.skill_level === skillFilter
  );

  // Get relevant process codes for this workflow
  const relevantCodes = WORKFLOW_PROCESSES[workflowType] || WORKFLOW_PROCESSES.standard;

  // Filter processes that match the workflow
  const relevantProcesses = processes?.filter((p: any) =>
    relevantCodes.includes(p.code)
  ).sort((a: any, b: any) => {
    const aIndex = relevantCodes.indexOf(a.code);
    const bIndex = relevantCodes.indexOf(b.code);
    return aIndex - bIndex;
  });

  const handleAssign = (processId: string, operatorId: string) => {
    const existing = assignments.find((a) => a.processId === processId);
    if (existing) {
      onAssignmentsChange(
        assignments.map((a) =>
          a.processId === processId ? { ...a, operatorId } : a
        )
      );
    } else {
      onAssignmentsChange([...assignments, { processId, operatorId }]);
    }
  };

  const getOperatorForProcess = (processId: string) => {
    return assignments.find((a) => a.processId === processId && !a.itemId)?.operatorId || '';
  };

  // Funciones para asignación por pieza
  const getOperatorForItemProcess = (itemId: string, processId: string) => {
    return assignments.find((a) => a.processId === processId && a.itemId === itemId)?.operatorId || '';
  };

  const handleAssignItemProcess = (itemId: string, processId: string, operatorId: string) => {
    const existingIndex = assignments.findIndex((a) => a.processId === processId && a.itemId === itemId);
    if (existingIndex >= 0) {
      const updated = [...assignments];
      updated[existingIndex] = { ...updated[existingIndex], operatorId };
      onAssignmentsChange(updated);
    } else {
      onAssignmentsChange([...assignments, { processId, operatorId, itemId }]);
    }
  };

  const assignedCount = assignments.filter((a) => a.operatorId).length;

  // Si el flujo pasa a ámbito plotter, descartar asignaciones de procesos bloqueados
  useEffect(() => {
    if (!isPlotterScope || !processes) return;
    const lockedIds = new Set(
      processes.filter((p: any) => isProcessLocked(p.code)).map((p: any) => p.id)
    );
    if (assignments.some((a) => lockedIds.has(a.processId))) {
      onAssignmentsChange(assignments.filter((a) => !lockedIds.has(a.processId)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlotterScope, processes, assignments]);

  // Validate required assignments and notify parent
  useEffect(() => {
    if (!onValidationChange || !assignmentRules || !relevantProcesses) return;

    const missingRequired: string[] = [];

    relevantProcesses.forEach((process: any) => {
      if (isProcessLocked(process.code)) return; // bloqueado: se asigna en la OT de laminado
      const rule = getRuleForProcess(process.code);
      const isRequired = rule?.assignment_type === 'required' ||
        // En ámbito plotter el operario de corte es obligatorio
        (isPlotterScope && process.code === 'material_cutting');
      if (isRequired) {
        const hasAssignment = assignments.some(
          a => a.processId === process.id && a.operatorId
        );
        if (!hasAssignment) {
          missingRequired.push(process.name);
        }
      }
    });

    onValidationChange(missingRequired.length === 0, missingRequired);
  }, [assignments, assignmentRules, relevantProcesses, onValidationChange, isPlotterScope]);

  // Count required processes and how many are assigned
  const requiredProcesses = relevantProcesses?.filter((p: any) =>
    !isProcessLocked(p.code) && (
      getRuleForProcess(p.code)?.assignment_type === 'required' ||
      (isPlotterScope && p.code === 'material_cutting')
    )
  ) || [];
  const assignedRequiredCount = requiredProcesses.filter((p: any) =>
    assignments.some(a => a.processId === p.id && a.operatorId)
  ).length;
  const hasUnassignedRequired = assignedRequiredCount < requiredProcesses.length;

  if (!processes || processes.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="flex items-center gap-2">
          <Users className="w-4 h-4" />
          Asignar Operarios por Proceso
          {hasUnassignedRequired && (
            <Badge variant="destructive" className="text-[10px] px-1.5 py-0 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              {requiredProcesses.length - assignedRequiredCount} obligatorio(s)
            </Badge>
          )}
        </Label>
        <div className="flex items-center gap-2">
          {showItemView && (
            <div className="flex border rounded-lg overflow-hidden">
              <Button
                type="button"
                variant={viewMode === 'process' ? 'default' : 'ghost'}
                size="sm"
                className="h-6 text-xs rounded-none px-2"
                onClick={() => setViewMode('process')}
              >
                Por Proceso
              </Button>
              <Button
                type="button"
                variant={viewMode === 'item' ? 'default' : 'ghost'}
                size="sm"
                className="h-6 text-xs rounded-none px-2"
                onClick={() => setViewMode('item')}
              >
                Por Pieza
              </Button>
            </div>
          )}
          <Select value={skillFilter} onValueChange={(v) => setSkillFilter(v as any)}>
            <SelectTrigger className="w-full sm:w-[100px] h-7 text-xs">
              <SelectValue placeholder="Nivel" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="A">Nivel A</SelectItem>
              <SelectItem value="B">Nivel B</SelectItem>
              <SelectItem value="C">Nivel C</SelectItem>
            </SelectContent>
          </Select>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setExpanded(!expanded)}
            className="h-7 text-xs"
          >
            {expanded ? (
              <>
                <ChevronUp className="w-4 h-4 mr-1" />
                Ocultar
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4 mr-1" />
                {assignedCount > 0 ? `${assignedCount} asignados` : 'Expandir'}
              </>
            )}
          </Button>
        </div>
      </div>

      {!expanded && assignedCount > 0 && (
        <div className="flex flex-wrap gap-1">
          {assignments
            .filter((a) => a.operatorId)
            .map((a) => {
              const process = processes?.find((p: any) => p.id === a.processId);
              // Verificar si es una asignación genérica o un operario específico
              const genericOption = GENERIC_OPERATOR_OPTIONS.find(opt => opt.id === a.operatorId);
              const operator = !genericOption ? operators?.find((o: any) => o.id === a.operatorId) : null;
              const displayName = genericOption ? genericOption.label : (operator?.full_name || 'Asignado');

              return (
                <Badge key={`${a.processId}-${a.itemId || 'global'}`} variant="secondary" className="text-xs">
                  {process?.name}: {displayName}
                </Badge>
              );
            })}
        </div>
      )}

      {expanded && (
        <Card>
          <CardContent className="p-3 space-y-3">
            <p className="text-xs text-muted-foreground">
              {viewMode === 'item'
                ? 'Asigna operarios diferentes para cada pieza y proceso. Los procesos marcados con * son obligatorios.'
                : 'Los procesos marcados como "Obligatorio" requieren un operario asignado. Los procesos "Pool" irán a la cola de trabajo si no se asignan.'}
            </p>

            {viewMode === 'item' && workOrderItems && workOrderItems.length > 0 ? (
              // Vista por pieza
              <div className="space-y-4 max-h-[350px] overflow-y-auto">
                {workOrderItems.map((item) => (
                  <div key={item.id} className="border rounded-lg p-3 bg-muted/20">
                    <div className="font-medium text-sm mb-2 flex items-center gap-2">
                      <Box className="w-4 h-4" />
                      Pieza #{item.item_number}
                      {item.part_label && (
                        <span className="text-muted-foreground font-normal">({item.part_label})</span>
                      )}
                    </div>
                    <div className="space-y-2">
                      {relevantProcesses?.map((process: any) => {
                        const rule = getRuleForProcess(process.code);
                        const isLocked = isProcessLocked(process.code);
                        const isRequired = !isLocked && rule?.assignment_type === 'required';
                        const isPool = rule?.assignment_type === 'pool';
                        const isNone = rule?.assignment_type === 'none';
                        const processOperators = getOperatorsForProcess(process.code);

                        if (isNone) return null;

                        if (isLocked) {
                          return (
                            <div key={process.id} className="flex items-center gap-2 opacity-50">
                              <Wrench className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                              <span className="text-sm min-w-[100px] truncate">{process.name}</span>
                              <span className="text-xs text-muted-foreground italic flex-1">
                                Se asignará en la OT de laminado
                              </span>
                            </div>
                          );
                        }

                        return (
                          <div key={process.id} className="flex items-center gap-2">
                            <Wrench className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                            <span className="text-sm min-w-[100px] truncate">{process.name}</span>
                            {isRequired && (
                              <Badge variant="destructive" className="text-[8px] px-1 py-0">*</Badge>
                            )}
                            <Select
                              value={getOperatorForItemProcess(item.id, process.id) || 'none'}
                              onValueChange={(val) =>
                                handleAssignItemProcess(item.id, process.id, val === 'none' ? '' : val)
                              }
                            >
                              <SelectTrigger className="h-7 text-xs flex-1">
                                <SelectValue placeholder={isPool ? "Pool" : "Sin asignar"} />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">{isPool ? "Dejar en pool" : "Sin asignar"}</SelectItem>

                                <SelectGroup>
                                  <SelectLabel className="text-[10px] text-muted-foreground flex items-center gap-1">
                                    <UserCog className="w-3 h-3" /> Por Tipo
                                  </SelectLabel>
                                  {GENERIC_OPERATOR_OPTIONS.map((opt) => (
                                    <SelectItem key={opt.id} value={opt.id}>
                                      <span className="flex items-center">
                                        {opt.label}
                                        {opt.skillLevel && getSkillBadge(opt.skillLevel as 'A' | 'B' | 'C')}
                                      </span>
                                    </SelectItem>
                                  ))}
                                </SelectGroup>

                                <SelectGroup>
                                  <SelectLabel className="text-[10px] text-muted-foreground flex items-center gap-1">
                                    <Users className="w-3 h-3" /> Operario Específico
                                  </SelectLabel>
                                  {processOperators?.map((operator: any) => (
                                    <SelectItem key={operator.id} value={operator.id}>
                                      <span className="flex items-center">
                                        {operator.full_name}
                                        {getSkillBadge(operator.skill_level)}
                                      </span>
                                    </SelectItem>
                                  ))}
                                </SelectGroup>
                              </SelectContent>
                            </Select>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              // Vista por proceso (existente)
              <div className="space-y-2 max-h-[250px] overflow-y-auto">
                {relevantProcesses?.map((process: any, index: number) => {
                  const rule = getRuleForProcess(process.code);
                  const isLocked = isProcessLocked(process.code);
                  const isRequired = !isLocked && (
                    rule?.assignment_type === 'required' ||
                    (isPlotterScope && process.code === 'material_cutting')
                  );
                  const isPool = !isLocked && rule?.assignment_type === 'pool';
                  const isNone = rule?.assignment_type === 'none';
                  const hasAssignment = assignments.some(a => a.processId === process.id && a.operatorId);
                  const processOperators = getOperatorsForProcess(process.code);

                  return (
                    <div
                      key={process.id}
                      className={cn(
                        'flex items-center gap-3 p-2 rounded-md',
                        index % 2 === 0 ? 'bg-muted/30' : '',
                        isLocked && 'opacity-50',
                        isRequired && !hasAssignment && 'ring-1 ring-red-300 bg-red-50/50 dark:bg-red-950/20'
                      )}
                    >
                      <div className="flex items-center gap-2 min-w-0 sm:min-w-[160px]">
                        <Wrench className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                        <span className="text-sm font-medium truncate">
                          {process.name}
                        </span>
                        {isRequired && (
                          <Badge variant="destructive" className="text-[9px] px-1 py-0 flex-shrink-0">
                            Obligatorio
                          </Badge>
                        )}
                        {isPool && (
                          <Badge variant="secondary" className="text-[9px] px-1 py-0 bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 flex-shrink-0">
                            Pool
                          </Badge>
                        )}
                      </div>

                      {isLocked ? (
                        <span className="text-xs text-muted-foreground italic flex-1">
                          Se asignará en la OT de laminado (paquete ya cortado)
                        </span>
                      ) : isNone ? (
                        <span className="text-xs text-muted-foreground italic flex-1">
                          Sin operario (automático)
                        </span>
                      ) : (
                        <Select
                          value={getOperatorForProcess(process.id) || 'none'}
                          onValueChange={(value) =>
                            handleAssign(process.id, value === 'none' ? '' : value)
                          }
                        >
                          <SelectTrigger className={cn(
                            "h-8 text-xs flex-1",
                            isRequired && !hasAssignment && "border-red-300"
                          )}>
                            <SelectValue placeholder={isPool ? "Pool (opcional)" : "Sin asignar"} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">{isPool ? "Dejar en pool" : "Sin asignar"}</SelectItem>

                            <SelectGroup>
                              <SelectLabel className="text-[10px] text-muted-foreground flex items-center gap-1">
                                <UserCog className="w-3 h-3" /> Por Tipo
                              </SelectLabel>
                              {GENERIC_OPERATOR_OPTIONS.map((opt) => (
                                <SelectItem key={opt.id} value={opt.id}>
                                  <span className="flex items-center">
                                    {opt.label}
                                    {opt.skillLevel && getSkillBadge(opt.skillLevel as 'A' | 'B' | 'C')}
                                  </span>
                                </SelectItem>
                              ))}
                            </SelectGroup>

                            <SelectGroup>
                              <SelectLabel className="text-[10px] text-muted-foreground flex items-center gap-1">
                                <Users className="w-3 h-3" /> Operario Específico
                              </SelectLabel>
                              {processOperators?.map((operator: any) => (
                                <SelectItem key={operator.id} value={operator.id}>
                                  <span className="flex items-center">
                                    {operator.full_name}
                                    {operator.employee_id && ` (${operator.employee_id})`}
                                    {getSkillBadge(operator.skill_level)}
                                  </span>
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
